/**
 * desktop-header.js
 */

import React from 'react';
import {
  useMediaQuery,
  Button as MuiButton,
  Box,
  Divider,
} from '@mui/material';
import { useRouteMatch } from 'react-router-dom';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import { ViewingMapContext } from 'components/tracking/map/viewing-map-context';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import PageTitle from 'components/page-title';
import Button from 'components/button';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import InfoCard from 'components/info-card';
import { RecordsContext } from './records-context';
import QuickLinks from './quick-links';


const ActionButton = (props) => {
  return (
    <Button sx={{ minWidth: 120, ml: 2 }} {...props} />
  )
}


export default function DesktopHeader(props) {
  const { addRecordMenuItems } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const { setIsReportDialogOpen } = React.useContext(RecordsContext);
  const { setMapParams, setIsDialogOpen } = React.useContext(ViewingMapContext);

  const basePath = useRouteMatch();
  const params = basePath.params;
  const filterOption = params.filterOption || '';

  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const hasLiveDeploymentTracks = status.stats.hasLiveDeploymentTracks.get();
  const showLiveMapLink = user.role.isSupervisor && hasLiveDeploymentTracks;
  const hasQuickLinks = (status.stats.incompleteCount.get() 
    + status.stats.notReviewedCount.get()
    + status.stats.lateCompletionCount.get()
    + status.stats.rejectedCount.get()
    + status.stats.outdatedCount.get()
    + status.stats.notReviewedCount.get()
    + status.stats.overdueVaccinationCount.get()
  ) > 0;


  if (isMobile) {
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={4}
    >
      <PageTitle
        iconName='records'
        title='Records'
        data-testid="records-title"
        style={{ paddingRight: 20 }}
      />

      <Box className='flex-hc'>

        {/* QUICK LINKS */}
        {hasQuickLinks &&
          <QuickLinks
            label={(quickLinkCount) =>
              <MuiButton data-testid='quick-links-button' style={{ padding: 0 }}>
                <InfoCard
                  iconId='todo'
                  value={quickLinkCount}
                  title='TO DO'
                />
              </MuiButton>
            }
            activeRecordFilterId={filterOption}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          />
        }

        {/* LIVE MAP BUTTON */}
        {showLiveMapLink &&
          <ActionButton
            variant='outlined'
            children='Live Map'
            startIcon={<Icon type='map' />}
            onClick={() => {
              setMapParams(TRACK_TYPE.MULTI_DEPLOYMENT);
              setIsDialogOpen(true);
            }}
          />
        }

        {(hasQuickLinks || showLiveMapLink) && <Divider orientation='vertical' flexItem sx={{ my: 0.75 }} />}

        {/* REPORT BUTTON */}
        <ActionButton
          variant='contained'
          color='secondary'
          startIcon={<Icon type='report' />}
          onClick={() => setIsReportDialogOpen(true)}
          children='Report'
        />

        {/* ADD RECORD DROPDOWN */}
        {!user.role.isSupervisor &&
          <DropMenu
            id="add-record-menu"
            items={addRecordMenuItems}
            label={(isOpen, onClick, arrowIcon) => 
              <ActionButton
                data-testid="add-record-button"
                variant="contained"
                aria-label="add record"
                startIcon={<Icon type='add' />}
                endIcon={arrowIcon}
                onClick={onClick}
                disabled={!user.role.status.isActive}
                children='Add Record'
              />}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          />            
        }
      </Box>
    </Box>
  )
} 